import { photoPath, type Photo } from '@baby-tracker/contracts';
import { useState, type ChangeEvent, type ReactElement } from 'react';

import { useDeletePhoto, usePhotos, useUploadPhoto } from '../../../hooks/useMedia.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { TextField } from '../../ui/TextField.js';
import styles from './insights.module.css';

async function imageSize(file: Blob): Promise<{ width?: number; height?: number }> {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
}

export function PhotosSection({
    familyId,
    childId,
    canLog,
}: {
    familyId: string;
    childId: string;
    canLog: boolean;
}): ReactElement {
    const photos = usePhotos(familyId, childId);
    const upload = useUploadPhoto(familyId);
    const remove = useDeletePhoto(familyId);
    const [caption, setCaption] = useState('');

    async function onFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        const size = await imageSize(file).catch(() => ({}));
        upload.mutate(
            {
                file,
                contentType: file.type,
                childId,
                ...(caption.length > 0 ? { caption } : {}),
                ...size,
            },
            {
                onSuccess: () => {
                    setCaption('');
                },
            },
        );
    }

    return (
        <Card title="Photos" headingLevel={2}>
            <p className={styles.meta}>
                Photos stay in your family album. Anyone in the family can view them.
            </p>
            {photos.error ? <FormError message={errorText(photos.error)} /> : null}
            {(photos.data?.length ?? 0) > 0 ? (
                <ul className={styles.album}>
                    {photos.data?.map((photo) => (
                        <PhotoTile
                            key={photo.id}
                            photo={photo}
                            canLog={canLog}
                            pending={remove.isPending}
                            onDelete={() => {
                                remove.mutate(photo.id);
                            }}
                        />
                    ))}
                </ul>
            ) : null}
            {canLog ? (
                <form className={fieldStyles.stack}>
                    <TextField
                        label="Caption"
                        value={caption}
                        onChange={(event) => {
                            setCaption(event.target.value);
                        }}
                    />
                    <label className={fieldStyles.label}>
                        Add a photo
                        <input
                            className={fieldStyles.control}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(event) => {
                                void onFile(event);
                            }}
                        />
                    </label>
                    {upload.error ? <FormError message={errorText(upload.error)} /> : null}
                    {remove.error ? <FormError message={errorText(remove.error)} /> : null}
                </form>
            ) : null}
        </Card>
    );
}

function PhotoTile({
    photo,
    canLog,
    pending,
    onDelete,
}: {
    photo: Photo;
    canLog: boolean;
    pending: boolean;
    onDelete: () => void;
}): ReactElement {
    return (
        <li className={styles.photo}>
            <img src={photoPath(photo.id)} alt={photo.caption ?? 'Family photo'} />
            {photo.caption ? <p className={styles.meta}>{photo.caption}</p> : null}
            {canLog ? (
                <Button variant="ghost" loading={pending} onClick={onDelete}>
                    Remove
                </Button>
            ) : null}
        </li>
    );
}
